import React from "react";
import { Link } from "react-router-dom";
import {
  Package, Plus, Download, Search, Truck, CheckCircle2,
  Clock, XCircle, ArrowRight, FileText, MapPin, CreditCard
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { CUSTOMER_ORDERS, CUSTOMER_INVOICES, COMPANY } from "../data/mock";

const STATUS_BADGE = {
  out: { label: "Out for delivery", className: "bg-[#f59321]/15 text-[#c46905]", Icon: Truck },
  delivered: { label: "Delivered", className: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  pending: { label: "Pending", className: "bg-slate-100 text-slate-600", Icon: Clock },
  picked: { label: "Picked up", className: "bg-blue-100 text-blue-700", Icon: Package },
  cancelled: { label: "Cancelled", className: "bg-red-100 text-red-700", Icon: XCircle }
};

function Badge({ statusKey }) {
  const s = STATUS_BADGE[statusKey] || STATUS_BADGE.pending;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${s.className}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </span>
  );
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const stats = [
    { label: "Active deliveries", value: "1", icon: Truck, color: "#f59321" },
    { label: "Delivered this month", value: "14", icon: CheckCircle2, color: "#10b981" },
    { label: "Open invoices", value: "$0", icon: CreditCard, color: "#0f2847" },
    { label: "Saved addresses", value: "6", icon: MapPin, color: "#6366f1" }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-white border-b border-slate-200">
        <div className="container-518 py-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-1">
                Customer portal
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0f2847]">
                Welcome back, {user?.name || "there"}.
              </h1>
              <p className="mt-1 text-slate-600">Here's what's moving today.</p>
            </div>
            <div className="flex gap-3">
              <Link to="/get-a-quote">
                <Button className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-12 px-6 font-semibold">
                  <Plus className="w-4 h-4 mr-1.5" />
                  New delivery
                </Button>
              </Link>
              <Link to="/tracking">
                <Button variant="outline" className="rounded-full h-12 px-5 font-semibold border-2">
                  <Search className="w-4 h-4 mr-1.5" />
                  Track
                </Button>
              </Link>
              <Link to="/compliance-center">
                <Button variant="outline" className="rounded-full h-12 px-5 font-semibold border-2" data-testid="customer-compliance-link">
                  <FileText className="w-4 h-4 mr-1.5" />
                  Documents
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-518 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-slate-200 card-lift">
              <div className="flex items-center justify-between mb-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ background: s.color }}>
                  <s.icon className="w-5 h-5" />
                </div>
              </div>
              <div className="font-display text-3xl font-extrabold text-[#0f2847]">{s.value}</div>
              <div className="mt-1 text-sm text-slate-500 font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="orders" className="mt-8">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-full h-12">
            <TabsTrigger value="orders" className="rounded-full px-5 h-10 font-semibold data-[state=active]:bg-[#0f2847] data-[state=active]:text-white">
              Deliveries
            </TabsTrigger>
            <TabsTrigger value="invoices" className="rounded-full px-5 h-10 font-semibold data-[state=active]:bg-[#0f2847] data-[state=active]:text-white">
              Invoices
            </TabsTrigger>
            <TabsTrigger value="addresses" className="rounded-full px-5 h-10 font-semibold data-[state=active]:bg-[#0f2847] data-[state=active]:text-white">
              Addresses
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
                <h3 className="font-display font-bold text-[#0f2847]">Recent deliveries</h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search orders" className="h-10 pl-9 w-64 rounded-full border-slate-200 text-sm" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">
                    <tr>
                      <th className="px-6 py-3">Order</th>
                      <th className="px-6 py-3">Route</th>
                      <th className="px-6 py-3">Service</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3 text-right">Total</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {CUSTOMER_ORDERS.map((o) => (
                      <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <div className="font-bold text-[#0f2847]">{o.id}</div>
                          <div className="text-xs text-slate-500">{o.date}</div>
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          <div className="font-medium">{o.from}</div>
                          <div className="text-xs text-slate-500">→ {o.to}</div>
                        </td>
                        <td className="px-6 py-4 text-slate-700">{o.service}</td>
                        <td className="px-6 py-4"><Badge statusKey={o.statusKey} /></td>
                        <td className="px-6 py-4 text-right font-bold text-[#0f2847]">{o.price}</td>
                        <td className="px-6 py-4 text-right">
                          <Link to="/tracking" className="text-[#f59321] font-bold hover:underline inline-flex items-center gap-1 text-xs">
                            Track <ArrowRight className="w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="invoices" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-display font-bold text-[#0f2847]">Invoices</h3>
                <Button variant="outline" size="sm" className="rounded-full h-9 font-semibold">
                  <Download className="w-4 h-4 mr-1.5" /> Download all
                </Button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">
                  <tr>
                    <th className="px-6 py-3">Invoice</th>
                    <th className="px-6 py-3">Period</th>
                    <th className="px-6 py-3">Deliveries</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Amount</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {CUSTOMER_INVOICES.map((inv) => (
                    <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4 font-bold text-[#0f2847]">{inv.id}</td>
                      <td className="px-6 py-4 text-slate-700">{inv.period}</td>
                      <td className="px-6 py-4 text-slate-700">{inv.deliveries}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" />
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-[#0f2847]">{inv.amount}</td>
                      <td className="px-6 py-4 text-right">
                        <button className="text-[#0f2847] hover:text-[#f59321] inline-flex items-center gap-1 text-xs font-bold">
                          <FileText className="w-3 h-3" /> PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="addresses" className="mt-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { label: "Office HQ", addr: "100 State St, Albany, NY 12207", primary: true },
                { label: "Warehouse", addr: "212 Broadway, Watervliet, NY 12189" },
                { label: "Branch — Saratoga", addr: "450 Broadway, Saratoga Springs, NY 12866" },
                { label: "Lab partner", addr: "43 New Scotland Ave, Albany, NY 12208" },
                { label: "Courthouse", addr: "612 State St, Schenectady, NY 12305" },
                { label: "Client — Hudson Parts", addr: "15 N Pearl St, Hudson, NY 12534" }
              ].map((a, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 card-lift">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-display font-bold text-[#0f2847]">{a.label}</h4>
                    {a.primary && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#f59321]/15 text-[#c46905]">Primary</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">{a.addr}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-10 p-6 rounded-2xl bg-[#0f2847] text-white stripe-bg flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-extrabold">Need a recurring route set up?</h3>
            <p className="text-slate-300 text-sm mt-1">Call dispatch at {COMPANY.phone} and we'll build the schedule with you.</p>
          </div>
          <Link to="/contact">
            <Button className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-11 px-5 font-semibold">
              Contact dispatch
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
